import { useTranslation } from "react-i18next";
import { Toaster } from "sonner";
import { ArrowDown, Download, Cpu, Atom, CircuitBoard, CheckCircle2, XCircle } from "lucide-react";
import ChipmatForm from "@/components/ChipmatForm";

const TRACK_ICONS = [Cpu, Atom, CircuitBoard];

export default function ChipMat() {
  const { t } = useTranslation();
  const arr = (k) => t(k, { returnObjects: true });

  const glance = [
    ["glance_grant", "glance_grant_v"], ["glance_period", "glance_period_v"], ["glance_trl", "glance_trl_v"],
    ["glance_portfolio", "glance_portfolio_v"], ["glance_equity", "glance_equity_v"], ["glance_mode", "glance_mode_v"],
  ];

  return (
    <div className="pb-24">
      <Toaster richColors position="top-right" />

      {/* Hero */}
      <section className="relative band-dark circuit-bg overflow-hidden" aria-labelledby="chipmat-title">
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-20 pb-16 lg:pt-24 lg:pb-20 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="tag mb-5 rise-in">{t("chipmat.eyebrow")}</div>
            <h1 id="chipmat-title" className="font-display font-black text-white leading-[0.95] rise-in rise-in-1">
              <span className="block text-6xl sm:text-7xl lg:text-8xl tracking-tight">{t("chipmat.title")}</span>
              <span className="block text-2xl sm:text-3xl lg:text-4xl text-amber mt-2">{t("chipmat.subtitle")}</span>
            </h1>
            <p className="mt-6 font-mono text-xs sm:text-sm uppercase tracking-[0.2em] text-white/80 rise-in rise-in-2">{t("chipmat.strap")}</p>
            <p className="mt-6 text-base md:text-lg text-white/85 max-w-2xl leading-relaxed rise-in rise-in-2">{t("chipmat.intro")}</p>
            <div className="mt-10 flex flex-wrap gap-3 rise-in rise-in-3">
              <a href="#apply" data-testid="chipmat-cta-apply" className="inline-flex items-center gap-2 bg-amber text-brand-deep px-6 py-3.5 font-semibold hover:brightness-105 transition">
                {t("chipmat.cta_apply")} <ArrowDown className="w-4 h-4" aria-hidden="true" />
              </a>
              <a href="/CHIPMAT-Commercialisation-Grant-Guidelines.docx" download data-testid="chipmat-cta-guidelines"
                 className="inline-flex items-center gap-2 border border-white/30 hover:bg-white/10 text-white px-6 py-3.5 font-medium transition-colors">
                <Download className="w-4 h-4" aria-hidden="true" /> {t("chipmat.cta_guidelines")}
              </a>
            </div>
          </div>
          <div className="lg:col-span-4 bg-white text-ink p-6 rise-in rise-in-4">
            <div className="font-mono text-[10px] uppercase tracking-widest text-teal mb-4">{t("chipmat.glance")}</div>
            <dl className="divide-y divide-ink/10">
              {glance.map(([k, v]) => (
                <div key={k} className="py-2.5 grid grid-cols-5 gap-3 text-sm">
                  <dt className="col-span-2 text-slate-500">{t(`chipmat.${k}`)}</dt>
                  <dd className="col-span-3 font-medium text-brand">{t(`chipmat.${v}`)}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      {/* Proposition */}
      <section className="py-16 border-b border-ink/10" aria-labelledby="prop-heading">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10">
          <h2 id="prop-heading" className="lg:col-span-4 font-display font-black text-brand text-3xl">{t("chipmat.proposition_title")}</h2>
          <p className="lg:col-span-8 text-ink/80 text-lg leading-relaxed">{t("chipmat.proposition")}</p>
        </div>
      </section>

      {/* Tracks */}
      <section className="py-16 bg-surface border-b border-ink/10" aria-labelledby="tracks-heading">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <h2 id="tracks-heading" className="font-display font-black text-brand text-3xl mb-10">{t("chipmat.tracks_title")}</h2>
          <div className="grid md:grid-cols-3 gap-4" data-testid="chipmat-tracks">
            {[1, 2, 3].map((n, i) => {
              const Icon = TRACK_ICONS[i];
              return (
                <article key={n} className={`bg-white border border-ink/10 p-7 border-t-4 rise-in rise-in-${i + 1}`} style={{ borderTopColor: ["#004888", "#00886A", "#F5A21C"][i] }}>
                  <div className="flex items-center justify-between mb-5">
                    <Icon className="w-6 h-6 text-brand" aria-hidden="true" />
                    <span className="font-mono text-[10px] text-ink/50">TRACK 0{n}</span>
                  </div>
                  <h3 className="font-display font-bold text-brand text-xl leading-snug">{t(`chipmat.track${n}`)}</h3>
                  <p className="mt-3 text-ink/75 text-sm leading-relaxed">{t(`chipmat.track${n}_d`)}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who / Not */}
      <section className="py-16 border-b border-ink/10" aria-label="Eligibility">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-10">
          <div>
            <h2 className="font-display font-bold text-brand text-2xl mb-6">{t("chipmat.who_title")}</h2>
            <ul className="space-y-3">
              {arr("chipmat.who").map((w, i) => (
                <li key={w || i} className="flex gap-3 text-ink/80 text-sm leading-relaxed"><CheckCircle2 className="w-5 h-5 text-teal shrink-0" aria-hidden="true" />{w}</li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="font-display font-bold text-brand text-2xl mb-6">{t("chipmat.not_title")}</h2>
            <ul className="space-y-3">
              {arr("chipmat.not").map((w, i) => (
                <li key={w || i} className="flex gap-3 text-ink/80 text-sm leading-relaxed"><XCircle className="w-5 h-5 text-red-500 shrink-0" aria-hidden="true" />{w}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Tranches + Evaluation */}
      <section className="py-16 bg-surface border-b border-ink/10" aria-label="Grant structure">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <h2 className="font-display font-bold text-brand text-2xl mb-6">{t("chipmat.tranches_title")}</h2>
            <Table head={[t("chipmat.tranche"), t("chipmat.share"), t("chipmat.trigger"), t("chipmat.evidence")]} rows={arr("chipmat.tranches")} testId="chipmat-tranches" />
          </div>
          <div className="lg:col-span-7">
            <h2 className="font-display font-bold text-brand text-2xl mb-6">{t("chipmat.eval_title")}</h2>
            <Table head={[t("chipmat.criterion"), t("chipmat.weight"), t("chipmat.key_q")]} rows={arr("chipmat.eval")} testId="chipmat-eval" />
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 border-b border-ink/10" aria-labelledby="process-heading">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <h2 id="process-heading" className="font-display font-bold text-brand text-2xl mb-10">{t("chipmat.process_title")}</h2>
          <ol className="grid md:grid-cols-3 lg:grid-cols-6 gap-px bg-ink/10 list-none p-0" data-testid="chipmat-process">
            {arr("chipmat.process").map(([h, d], i) => (
              <li key={h || i} className="bg-white p-6">
                <div className="font-display font-black text-teal text-3xl mb-3">0{i + 1}</div>
                <h3 className="font-display font-bold text-brand text-base">{h}</h3>
                <p className="mt-2 text-ink/70 text-xs leading-relaxed">{d}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Support + governance */}
      <section className="py-16 bg-surface border-b border-ink/10" aria-label="Support and governance">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-10">
          <div>
            <h2 className="font-display font-bold text-brand text-2xl mb-6">{t("chipmat.support_title")}</h2>
            <div className="space-y-4">
              {["support_tech", "support_com", "support_ip"].map((k, i) => (
                <div key={k} className="border-l-2 border-teal pl-5">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-teal mb-1">0{i + 1}</div>
                  <p className="text-ink/80 text-sm leading-relaxed">{t(`chipmat.${k}`)}</p>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h2 className="font-display font-bold text-brand text-2xl mb-6">{t("chipmat.governance_title")}</h2>
            <dl className="divide-y divide-ink/10 border border-ink/10 bg-white">
              {arr("chipmat.governance").map(([h, d]) => (
                <div key={h} className="p-4 grid sm:grid-cols-5 gap-2">
                  <dt className="sm:col-span-2 font-medium text-brand text-sm">{h}</dt>
                  <dd className="sm:col-span-3 text-ink/75 text-sm">{d}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      {/* Apply */}
      <section id="apply" className="py-20 scroll-mt-20" aria-labelledby="apply-heading">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-28">
              <div className="tag mb-3">{t("chipmat.form_eyebrow")}</div>
              <h2 id="apply-heading" className="font-display font-black text-brand text-3xl sm:text-4xl">{t("chipmat.form_title")}</h2>
              <p className="mt-5 text-ink/75 leading-relaxed">{t("chipmat.form_intro")}</p>
            </div>
          </div>
          <div className="lg:col-span-8 border border-ink/10 bg-surface p-6 lg:p-10">
            <ChipmatForm />
          </div>
        </div>
      </section>
    </div>
  );
}

function Table({ head, rows, testId }) {
  return (
    <div className="border border-ink/10 bg-white overflow-x-auto" data-testid={testId}>
      <table className="w-full text-sm">
        <thead className="bg-brand text-white text-left font-mono text-[10px] uppercase tracking-widest">
          <tr>{head.map((h) => <th key={h} className="p-3 font-medium">{h}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-ink/10">
          {rows.map((r, i) => (
            <tr key={r[0] || i} className="align-top hover:bg-surface">
              {r.map((c, j) => <td key={head[j] || j} className={`p-3 ${j === 1 ? "font-display font-bold text-teal whitespace-nowrap" : "text-ink/80"}`}>{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
